/**
 * The token list: pure logic (no network, no React). The server feeds it with the CADINUdex pools (on-chain figures), market data for the
 * listed tokens, the competition counts and the project-info flags; the client sorts and filters the result locally, so switching
 * Hot on CADINUdex / Liquidity / Market cap / Most competitions is instant.
 *
 * Two lists come out of one build (Bez, 2026-10-08): "Hot on CADINUdex" (`buildListItems`: CADINUdex tokens above the floor plus the
 * pinned tokens, exactly as before) and "All token pages on CADINU" (`buildPageItems`: every token with a registered page, verified or
 * basic). A basic page never pins a token into the hot list and never gets a tick.
 */
import { isMajorToken, lc } from 'views/TradingCompetitionV3/pool-search/logic'
import type { Address, CompetitionCounts, ListItem, ListResponse, SortKey } from './types'
import { listVerifiedOf } from './verification'

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/
const num = (x: unknown): number | null => {
  if (x === null || x === undefined || x === '') return null
  const n = Number(x)
  return Number.isFinite(n) ? n : null
}
/** GeckoTerminal answers 0 for a cap it does not know: that is "unknown", not a market cap of zero. */
const pos = (x: unknown): number | null => {
  const n = num(x)
  return n !== null && n > 0 ? n : null
}

export type GeckoTokenLite = {
  address: Address
  symbol: string
  name: string
  logo: string | null
  priceUsd: number | null
  fdvUsd: number | null
  marketCapUsd: number | null
  liquidityUsd: number | null
  volume24hUsd: number | null
}
/** /networks/bsc/tokens/multi/{a,b,...} answer. */
export function parseGeckoTokens(json: any): GeckoTokenLite[] {
  const out: GeckoTokenLite[] = []
  for (const t of json?.data ?? []) {
    const a = t?.attributes
    if (!a?.address || !ADDRESS_RE.test(a.address)) continue
    out.push({
      address: a.address as Address,
      symbol: String(a.symbol ?? '').trim(),
      name: String(a.name ?? a.symbol ?? '').trim(),
      logo: typeof a.image_url === 'string' && a.image_url.startsWith('https://') ? a.image_url : null,
      priceUsd: num(a.price_usd),
      fdvUsd: pos(a.fdv_usd),
      marketCapUsd: pos(a.market_cap_usd),
      liquidityUsd: num(a.total_reserve_in_usd),
      volume24hUsd: num(a.volume_usd?.h24),
    })
  }
  return out
}

const emptyCounts: CompetitionCounts = { live: 0, upcoming: 0, finished: 0, total: 0 }

/**
 * What the list keeps per token with a registered page: its logo, and for approved info the ownership signature and its ONE tick (gold /
 * blue). `basic` marks a basic page (links from public profiles, written by the CADINU team): no tick, no signature, never a pin.
 */
export type InfoFlags = { teamVerified: boolean; logoUrl?: string | null; verified?: 'gold' | 'blue'; basic?: boolean; cad1?: boolean }

/**
 * The token_pages `/tokens?include=basic` answer (ONE request per list build; one row per token: the current approved info first, then the
 * basic pages marked `basic: true`) by lowercase address. The tick comes from an approved row: gold when `cadinuVerified`, else blue
 * (verification.ts `listVerifiedOf`); a basic row gets no tick whatever else it says. Rows without a valid address are dropped; garbage
 * gives an empty map. An older API without the parameter answers the approved rows alone, which still parse.
 */
export function parseInfoRows(json: any): Record<string, InfoFlags> {
  const out: Record<string, InfoFlags> = {}
  for (const r of Array.isArray(json?.results) ? json.results : []) {
    if (!r || typeof r.token !== 'string' || !ADDRESS_RE.test(r.token)) continue
    const key = lc(r.token)
    if (out[key]) continue // the API sends the current info first
    const logoUrl = typeof r.logoUrl === 'string' ? r.logoUrl : null
    // a CAD1 launch info row (Bez, 2026-10-10: bound by the token's on-chain hash, published without review) carries a logo, never a tick
    out[key] =
      r.basic === true
        ? { teamVerified: false, logoUrl, basic: true }
        : r.cad1 === true
        ? { teamVerified: false, logoUrl, cad1: true }
        : { teamVerified: r.teamVerified === true, logoUrl, verified: listVerifiedOf(r) ?? undefined }
  }
  return out
}

/**
 * The two token_pages answers of a list build: `?include=basic` (approved info, then basic pages) and `?include=cad1` (CAD1 launch info).
 * A CAD1 row replaces a basic page of the same token, never approved info. Only rows marked `cad1: true` are taken from the second answer:
 * an older API that does not know `include=cad1` answers its approved rows there, which change nothing.
 */
export function mergeInfoRows(basicAnswer: any, cad1Answer: any): Record<string, InfoFlags> {
  const out = parseInfoRows(basicAnswer)
  const cad1 = parseInfoRows({ results: (Array.isArray(cad1Answer?.results) ? cad1Answer.results : []).filter((r: any) => r?.cad1 === true) })
  for (const [key, flags] of Object.entries(cad1)) {
    if (!out[key] || out[key].basic) out[key] = flags
  }
  return out
}

/**
 * The tokens listed whatever their pools: the brand tokens, the tokens with a competition and the tokens with APPROVED info. A basic
 * page is not a reason (Bez, 2026-10-03 anti-spam rule for "Hot on CADINUdex"); it is listed under "All token pages" instead.
 */
export function pinnedTokens(brand: string[], competitions: Record<string, unknown>, info: Record<string, InfoFlags>): string[] {
  const approved = Object.keys(info).filter((a) => !info[a]?.basic && !info[a]?.cad1)
  return Array.from(new Set([...brand, ...Object.keys(competitions), ...approved].map(lc))).filter((a) => ADDRESS_RE.test(a))
}

/**
 * Anti-spam floor for "Hot on CADINUdex" (Bez, 2026-10-03): a token joins the list from its CADINUdex pools only when those pools hold at
 * least this much liquidity together (on-chain TVL from server/info). $2 sits between the smallest real pools (about $3) and the dust and
 * test pools in the registry (BTCB/USDT $0.19, ETH/USDT $0.14, FIREMOON or Test1/Test2 below a cent or unpriced). Pinned tokens skip it.
 */
export const HOT_MIN_LIQUIDITY_USD = 2

/** One CADINUdex pool as server/info reads it on chain (V2 or V3). */
export type CadinudexPool = {
  address: string
  version: 'v2' | 'v3'
  token0: { address: string; symbol: string; name: string }
  token1: { address: string; symbol: string; name: string }
  tvlUsd: number | null
  volume24hUsd: number | null
  trades24h: number
}

export type HotToken = {
  address: Address
  symbol: string
  name: string
  liquidityUsd: number
  volume24hUsd: number | null
  trades24h: number
  dexNames: string[]
  /** 1 = hottest */
  rank: number
}

/**
 * The tokens of CADINUdex pools, hottest first: 24 h volume on CADINUdex, then 24 h trades, then liquidity. Every non-major side of a pool
 * counts (a CADINU/CBON pool counts for both); majors (WBNB, USDT, USDC, BUSD, BTCB, ETH) are never listed. A token whose pools hold less
 * than `minLiquidityUsd` together is left out.
 */
export function hotTokens(pools: CadinudexPool[], minLiquidityUsd = HOT_MIN_LIQUIDITY_USD): HotToken[] {
  const by = new Map<string, Omit<HotToken, 'rank' | 'dexNames'> & { versions: Set<string>; pools: Set<string> }>()
  for (const p of pools) {
    for (const t of [p.token0, p.token1]) {
      if (!ADDRESS_RE.test(t.address) || isMajorToken(t.address) || !t.symbol) continue
      const key = lc(t.address)
      const it = by.get(key) ?? { address: t.address as Address, symbol: t.symbol, name: t.name || t.symbol, liquidityUsd: 0, volume24hUsd: null, trades24h: 0, versions: new Set<string>(), pools: new Set<string>() }
      by.set(key, it)
      const pk = lc(p.address)
      if (it.pools.has(pk)) continue // a pool of a token with itself is counted once
      it.pools.add(pk)
      it.versions.add(p.version)
      if (p.tvlUsd !== null && Number.isFinite(p.tvlUsd) && p.tvlUsd > 0) it.liquidityUsd += p.tvlUsd
      if (p.volume24hUsd !== null && Number.isFinite(p.volume24hUsd)) it.volume24hUsd = (it.volume24hUsd ?? 0) + p.volume24hUsd
      it.trades24h += p.trades24h || 0
    }
  }
  const out = Array.from(by.values())
    .filter((t) => t.liquidityUsd >= minLiquidityUsd)
    .sort((a, b) => (b.volume24hUsd ?? 0) - (a.volume24hUsd ?? 0) || b.trades24h - a.trades24h || b.liquidityUsd - a.liquidityUsd || a.symbol.localeCompare(b.symbol))
  return out.map(({ versions, pools: _p, ...t }, i) => ({ ...t, dexNames: ['v3', 'v2'].filter((v) => versions.has(v)).map((v) => `CADINUdex ${v.toUpperCase()}`), rank: i + 1 }))
}

export type ListInput = {
  /** CADINUdex pools with on-chain figures (server/info); the only source of non-pinned tokens */
  cadinudexPools: CadinudexPool[]
  /** CADINUdex prices per token (server/info), used when GeckoTerminal has none */
  cadinudexPrices: Record<string, number | null>
  /** tokens with a CADINUdex pool (pool registry): the CADINUdex mark when the pool figures are unavailable */
  cadinudexTokens: string[]
  /** market data of the listed tokens (GeckoTerminal multi-token endpoint, asked only for these addresses) */
  tokens: GeckoTokenLite[]
  competitions: Record<string, CompetitionCounts>
  /** tokens with a registered page (token_pages, read once per list build): approved info with its tick, or a basic page */
  info: Record<string, InfoFlags>
  /** tokens listed whatever their pools (brand tokens, competition tokens, tokens with approved info; see `pinnedTokens`) */
  pinned: string[]
}

/**
 * The list: "Hot on CADINUdex" (tokens of CADINUdex pools above the floor) plus the pinned tokens. Nothing else: no exchange-wide trending
 * or new-pool feed, so a random token reaches the list only by having a real CADINUdex pool. Liquidity and volume on the card are the
 * token's figures elsewhere (GeckoTerminal) plus its CADINUdex figures: GeckoTerminal does not index CADINUdex pools (CADINU: $16 there
 * against $1,076 in its CADINUdex pools on 2026-10-03), so the sum counts nothing twice. Price and caps come from GeckoTerminal, else CADINUdex.
 */
const plus = (x: number | null | undefined, y: number | null | undefined) => (typeof x !== 'number' && typeof y !== 'number' ? null : (x ?? 0) + (y ?? 0))

/** What both lists read per token, built once per `ListInput`. */
function contextOf(input: ListInput) {
  const hot = hotTokens(input.cadinudexPools)
  return {
    hot,
    hotBy: new Map(hot.map((h) => [lc(h.address), h])),
    onCadinudex: new Set([...input.cadinudexTokens.map(lc), ...input.cadinudexPools.flatMap((p) => [lc(p.token0.address), lc(p.token1.address)])]),
    market: new Map(input.tokens.map((t) => [lc(t.address), t])),
  }
}

/** One token as the list shows it; the same card data whichever list it is in. */
function itemOf(key: string, input: ListInput, ctx: ReturnType<typeof contextOf>): ListItem {
  const h = ctx.hotBy.get(key)
  const m = ctx.market.get(key)
  const c = input.competitions[key]
  const i = input.info[key]
  // a basic page carries a logo but never the tick, the ownership signature or the "has info" state
  const approved = i && !i.basic && !i.cad1 ? i : undefined
  const symbol = m?.symbol || h?.symbol || `${key.slice(0, 6)}…${key.slice(-4)}`
  const cadinudexPrice = input.cadinudexPrices[key] ?? null
  const on = ctx.onCadinudex.has(key)
  const dexNames = h?.dexNames ?? (on ? ['CADINUdex'] : [])
  return {
    address: (m?.address ?? h?.address ?? key) as Address,
    symbol,
    name: m?.name || h?.name || (m?.symbol || h?.symbol ? symbol : 'Unnamed token'),
    logoUrl: i?.logoUrl || m?.logo || null,
    priceUsd: m?.priceUsd ?? cadinudexPrice,
    change24h: null,
    marketCapUsd: m?.marketCapUsd ?? null,
    fdvUsd: m?.fdvUsd ?? null,
    liquidityUsd: plus(m?.liquidityUsd, h?.liquidityUsd),
    volume24hUsd: plus(m?.volume24hUsd, h?.volume24hUsd),
    trades24h: h ? h.trades24h : null,
    hotRank: h?.rank ?? null,
    dexNames,
    onCadinudex: on,
    competitions: c ?? { ...emptyCounts },
    hasInfo: Boolean(approved),
    teamVerified: Boolean(approved?.teamVerified),
    // a token with approved info has a tick: gold when the team vetted it, else blue (an older input without the field: blue)
    verified: approved ? approved.verified ?? 'blue' : null,
    page: approved ? 'verified' : i?.cad1 ? 'cad1' : i?.basic ? 'basic' : null,
  }
}

export function buildListItems(input: ListInput): ListItem[] {
  const ctx = contextOf(input)
  const pinned = input.pinned.map(lc).filter((a) => ADDRESS_RE.test(a) && !isMajorToken(a))
  const keys = Array.from(new Set([...ctx.hot.map((h) => lc(h.address)), ...pinned]))
  return keys.map((key) => itemOf(key, input, ctx))
}

/**
 * "All token pages on CADINU" (Bez, 2026-10-08): every token with a registered page, verified (approved info, its tick) or basic (no
 * tick, labelled), with the same figures as the hot list where known. The hot list is not changed by this: a basic page is never a pin.
 */
export function buildPageItems(input: ListInput): ListItem[] {
  const ctx = contextOf(input)
  const keys = Object.keys(input.info).map(lc).filter((a) => ADDRESS_RE.test(a) && !isMajorToken(a))
  return Array.from(new Set(keys)).map((key) => itemOf(key, input, ctx))
}

/** Both lists as one, the hot list first and each token once: what a search box suggests from. */
export function listedItems(data?: Pick<ListResponse, 'items' | 'pages'> | null): ListItem[] {
  const seen = new Set<string>()
  const out: ListItem[] = []
  for (const i of [...(data?.items ?? []), ...(data?.pages ?? [])]) {
    const key = lc(i.address)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(i)
  }
  return out
}

// ---------------------------------------------------------------------------------------------- sort and search

const desc = (a: number | null, b: number | null) => (b ?? -Infinity) - (a ?? -Infinity)
const tie = (a: ListItem, b: ListItem) => a.symbol.localeCompare(b.symbol) || lc(a.address).localeCompare(lc(b.address))

const cmp: Record<SortKey, (a: ListItem, b: ListItem) => number> = {
  // the CADINUdex ranking first (24 h volume, trades, liquidity on CADINUdex pools), then the pinned tokens by volume and liquidity
  hot: (a, b) => (a.hotRank ?? 1e9) - (b.hotRank ?? 1e9) || desc(a.volume24hUsd, b.volume24hUsd) || desc(a.liquidityUsd, b.liquidityUsd),
  liquidity: (a, b) => desc(a.liquidityUsd, b.liquidityUsd),
  mcap: (a, b) => desc(a.marketCapUsd ?? a.fdvUsd, b.marketCapUsd ?? b.fdvUsd),
  competitions: (a, b) =>
    b.competitions.live - a.competitions.live || b.competitions.total - a.competitions.total || b.competitions.upcoming - a.competitions.upcoming || desc(a.volume24hUsd, b.volume24hUsd),
}

export function sortItems(items: ListItem[], sort: SortKey): ListItem[] {
  return [...items].sort((a, b) => cmp[sort](a, b) || tie(a, b))
}

/** gold, then blue, then no tick */
const verifiedRank = (i: ListItem) => (i.verified === 'gold' ? 0 : i.verified === 'blue' ? 1 : 2)

/**
 * "All token pages": verified pages first (gold, then blue), then by liquidity; the sort chips apply within that where they mean something
 * (Liquidity, Market cap, Most competitions). "Hot on CADINUdex" has no meaning for a page without a CADINUdex pool, so it sorts by liquidity.
 */
export function sortPages(items: ListItem[], sort: SortKey): ListItem[] {
  const by = sort === 'hot' ? 'liquidity' : sort
  return [...items].sort((a, b) => verifiedRank(a) - verifiedRank(b) || cmp[by](a, b) || tie(a, b))
}

/** Symbol, name or address. An address needs at least 6 characters to match, so "0x" lists everything. */
export function filterItems(items: ListItem[], q: string): ListItem[] {
  const needle = q.trim().toLowerCase().replace(/^\$/, '')
  if (!needle) return items
  const addressLike = needle.startsWith('0x')
  if (addressLike && needle.length < 6) return items
  return items.filter((i) => (addressLike ? lc(i.address).includes(needle) : i.symbol.toLowerCase().includes(needle) || i.name.toLowerCase().includes(needle)))
}
